<template>
  <div class="account-management">
    <PageHeader title="账户管理" />
    <el-card>
      <template #header>
        <div style="display:flex;justify-content:space-between;align-items:center;">
          <div class="account-title">{{ form.accountName }}（{{ form.accountNo }}）</div>
          <div>
            <el-button type="warning" @click="showWithdraw=true" v-if="hasPermission('sys:account:withdraw')">提现</el-button>
            <el-button type="primary" @click="showInvest=true" v-if="hasPermission('sys:account:invest')">出资</el-button>
            <el-button type="success" @click="showRecharge=true" v-if="hasPermission('sys:account:recharge')">充值</el-button>
          </div>
        </div>
      </template>
      <div class="stats">
        <div class="stat-card">
          <div class="stat-label">累计投资金额</div>
          <div class="stat-value">{{ currency(form.cumInvestAmount) }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">累计回收金额</div>
          <div class="stat-value">{{ currency(form.cumRecoveredAmount) }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">累计提现金额</div>
          <div class="stat-value">{{ currency(form.cumWithdrawAmount) }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">账户可用余额</div>
          <div class="stat-value">{{ currency(form.availableBalance) }}</div>
        </div>
      </div>
    </el-card>

    <el-card style="margin-top:16px">
      <template #header>关联卡管理</template>
      <div style="margin-bottom:8px">
        <el-button type="primary" @click="showCardDialog=true" v-if="hasPermission('sys:account:addCard')">新增卡</el-button>
      </div>
      <el-table :data="cards" border>
        <el-table-column prop="id" label="ID" width="80"/>
        <el-table-column prop="bankName" label="银行"/>
        <el-table-column prop="cardNo" label="卡号"/>
        <el-table-column prop="holderName" label="持卡人"/>
        <el-table-column prop="status" label="状态">
          <template #default="scope">
            <span>{{ scope.row.status === 1 ? '正常' : '禁用' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="160">
          <template #default="scope">
            <el-button type="text" @click="removeCard(scope.row.id)" v-if="hasPermission('sys:account:removeCard')">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-card style="margin-top:16px">
      <template #header>已投项目情况</template>
      <el-table :data="investedProjects" border>
        <el-table-column prop="agreementNo" label="协议编号" min-width="140" show-overflow-tooltip/>
        <el-table-column prop="investAmount" label="投资金额" min-width="110" align="right">
          <template #default="scope">
            {{ currency(scope.row.investAmount) }}
          </template>
        </el-table-column>
        <el-table-column prop="investDate" label="出资日期" min-width="110">
          <template #default="scope">
            {{ formatDate(scope.row.investDate) }}
          </template>
        </el-table-column>
        <el-table-column prop="recoveredAmount" label="已回收金额" min-width="110" align="right">
          <template #default="scope">
            {{ currency(scope.row.recoveredAmount) }}
          </template>
        </el-table-column>
        <el-table-column prop="status" label="项目状态" min-width="90">
          <template #default="scope">
            <el-tag :type="scope.row.status === '进行中' ? 'success' : 'info'">
              {{ scope.row.status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="jointStartTime" label="联营开始日期" min-width="110">
          <template #default="scope">
            {{ formatDate(scope.row.jointStartTime) }}
          </template>
        </el-table-column>
        <el-table-column prop="jointEndTime" label="联营结束日期" min-width="110">
          <template #default="scope">
            {{ formatDate(scope.row.jointEndTime) }}
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-card style="margin-top:16px">
      <template #header>账户流水</template>
      <div ref="chartRef" style="height:400px;margin-bottom:16px"></div>
      <div style="margin-bottom:8px;display:flex;gap:8px;align-items:center">
        <el-date-picker v-model="dateRange" type="daterange" value-format="YYYY-MM-DD" />
        <el-select v-model="types" multiple placeholder="类型">
          <el-option label="投资" value="INVEST"/>
          <el-option label="回收" value="RECOVERY"/>
          <el-option label="提现" value="WITHDRAW"/>
          <el-option label="调整" value="ADJUST"/>
        </el-select>

        <el-button type="primary" @click="loadTx">查询</el-button>
        <el-button @click="exportCsv">导出CSV</el-button>
      </div>
      <el-table :data="txList" border style="width: 100%">
        <!-- 所有列都通过动态列显示 -->
        <el-table-column
          v-for="column in dynamicColumns"
          :key="column.prop"
          :prop="column.prop"
          :label="column.label"
          :align="column.align"
          min-width="120"
        >
          <template #default="scope">
            <!-- 根据字段类型和特殊字段进行格式化 -->
            {{ formatCellValue(scope.row[column.prop], column) }}
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        style="margin-top:8px"
        :current-page="pageNum"
        :page-size="pageSize"
        :total="total"
        layout="prev, pager, next"
        @current-change="onPageChange"/>
    </el-card>

    <el-dialog v-model="showCardDialog" title="新增卡" width="420px">
      <el-form :model="cardForm" label-width="100px">
        <el-form-item label="银行"><el-input v-model="cardForm.bankName"/></el-form-item>
        <el-form-item label="卡号"><el-input v-model="cardForm.cardNo"/></el-form-item>
        <el-form-item label="持卡人"><el-input v-model="cardForm.holderName"/></el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showCardDialog=false">取消</el-button>
        <el-button type="primary" @click="addCard">保存</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="showWithdraw" title="提现" width="420px">
      <el-form :model="withdrawForm" label-width="100px">
        <el-form-item label="当前可用余额"><span>{{ currency(form.availableBalance) }}</span></el-form-item>
        <el-form-item label="提现金额"><el-input v-model="withdrawForm.amount" placeholder="请输入金额"/></el-form-item>
        <el-form-item label="备注"><el-input v-model="withdrawForm.memo"/></el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showWithdraw=false">取消</el-button>
        <el-button type="primary" @click="doWithdraw">确认</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="showInvest" title="出资" width="420px">
      <el-form :model="investForm" label-width="100px">
        <el-form-item label="当前可用余额"><span>{{ currency(form.availableBalance) }}</span></el-form-item>
        <el-form-item v-if="isSuperTenant" label="目标租户">
          <el-select v-model="investForm.targetTenantId" placeholder="请选择租户" clearable>
            <el-option v-for="tenant in tenants" :key="tenant.id" :label="tenant.tenantName" :value="tenant.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="可用协议">
          <el-select v-model="investForm.agreementNo" placeholder="请选择协议" clearable>
            <el-option v-for="agreement in agreements" :key="agreement.agreementNo" :label="agreement.agreementName" :value="agreement.agreementNo" />
          </el-select>
        </el-form-item>
        <el-form-item label="出资金额"><el-input v-model="investForm.amount" placeholder="请输入金额"/></el-form-item>
        <el-form-item label="备注"><el-input v-model="investForm.memo"/></el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showInvest=false">取消</el-button>
        <el-button type="primary" @click="doInvest">确认</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="showRecharge" title="充值" width="420px">
      <el-form :model="rechargeForm" label-width="100px">
        <el-form-item label="当前可用余额"><span>{{ currency(form.availableBalance) }}</span></el-form-item>
        <el-form-item label="充值金额"><el-input v-model="rechargeForm.amount" placeholder="请输入金额"/></el-form-item>
        <el-form-item label="备注"><el-input v-model="rechargeForm.memo"/></el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showRecharge=false">取消</el-button>
        <el-button type="primary" @click="doRecharge">确认</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted, onUnmounted, computed } from 'vue'
import { ElMessage } from 'element-plus'
import * as echarts from 'echarts'
import PageHeader from '../../components/PageHeader.vue'
import accountService from '../../services/accountService'
import store from '../../store'
import tenantService from '../../services/tenantService'
import { formatDate } from '../../utils/dateUtils'

export default {
  name: 'AccountManagement',
  components: { PageHeader },
  setup() {
    const form = reactive({
      accountName: '',
      accountNo: '',
      cumInvestAmount: 0,
      cumRecoveredAmount: 0,
      cumWithdrawAmount: 0,
      availableBalance: 0
    })
    const cards = ref([])
    const showCardDialog = ref(false)
    const cardForm = reactive({ bankName: '', cardNo: '', holderName: '' })
    const dateRange = ref([])
    const types = ref([])
    const txList = ref([])
    const pageNum = ref(1)
    const pageSize = ref(10)
    const total = ref(0)
    const showWithdraw = ref(false)
    const withdrawForm = reactive({ amount: '', memo: '' })
    const showInvest = ref(false)
    const investForm = reactive({ agreementNo: '', amount: '', memo: '', targetTenantId: '' })
    const showRecharge = ref(false)
    const rechargeForm = reactive({ amount: '', memo: '' })
    const agreements = ref([])
    const tenants = ref([])
    const currentTenantId = ref(store.state.tenantId || '')
    const isSuperTenant = currentTenantId.value === '1'
    const chartRef = ref(null)
    let chart = null
    const chartData = ref({ dates: [], balances: [], amounts: [] })
    const investedProjects = ref([])
    // 动态列定义
    const dynamicColumns = ref([])
    
    // 交易类型映射
    const txTypeMap = {
      'INVEST': '投资',
      'RECOVERY': '收入分成',
      'WITHDRAW': '提现',
      'ADJUST': '调整',
      'RECHARGE': '充值'
    }

    const currency = (v) => {
      const n = Number(v || 0)
      return n.toLocaleString('zh-CN', { style: 'currency', currency: 'CNY', maximumFractionDigits: 2 })
    }
    
    // 格式化单元格值
    const formatCellValue = (value, column) => {
      // 处理空值
      if (value === null || value === undefined) {
        return '-'
      }
      
      // 特殊字段处理
      if (column.prop === 'txType') {
        return txTypeMap[value] || value
      } else if (column.prop === 'direction') {
        return value === 'IN' ? '入账' : '出账'
      } else if (column.prop === 'devideRate' || column.prop === 'tenantShareRatio') {
        return (Number(value) * 100).toFixed(2) + '%'
      }
      
      // 根据字段类型处理
      switch (column.type) {
        case 'date':
          return formatDate(value)
        case 'amount':
          return currency(value)
        case 'number':
          return Number(value).toLocaleString('zh-CN')
        case 'boolean':
          return value ? '是' : '否'
        default:
          return value
      }
    }
    
    // 从后端获取图表数据
    const loadChartData = async () => {
      try {
        const params = {
          startDate: dateRange.value?.[0],
          endDate: dateRange.value?.[1],
          types: types.value
        }
        const res = await accountService.getChartData(params)
        if (res && res.code === 200) {
          // 假设后端返回的数据格式为 { dates: [], balances: [], amounts: [] }
          // 其中dates已经是格式化好的日期字符串
          chartData.value = res.data || { dates: [], balances: [], amounts: [] }
        } else {
          chartData.value = { dates: [], balances: [], amounts: [] }
        }
      } catch (e) {
        console.error('获取图表数据失败:', e)
        chartData.value = { dates: [], balances: [], amounts: [] }
      }
    }
    
    // 渲染图表
    const renderChart = () => {
      if (!chartRef.value) return
      
      if (!chart) {
        chart = echarts.init(chartRef.value)
        // 添加窗口大小变化监听
        window.addEventListener('resize', () => chart && chart.resize())
      }
      
      const { dates, balances, amounts } = chartData.value
      
      const option = {
        tooltip: {
          trigger: 'axis',
          formatter: function(params) {
            let result = params[0].axisValue + '<br/>'
            params.forEach(function(param) {
              let value = param.seriesName === '余额' ? currency(param.value) : currency(param.value)
              result += param.marker + param.seriesName + ': ' + value + '<br/>'
            })
            return result
          }
        },
        legend: {
          data: ['余额', '金额'],
          top: 0
        },
        grid: {
          left: '5%',
          right: '4%',
          bottom: '3%',
          top: '15%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: dates
        },
        yAxis: [
          {
            type: 'value',
            name: '余额',
            scale: true,
            min: function(value) {
              // 设置Y轴起始点为数据最小值的0.9倍，增强变化可见性
              return Math.floor(value.min * 0.99);
            },
            axisLabel: {
              formatter: (value) => {
                return (value / 10000).toFixed(0) + '万'
              }
            },
            splitNumber: 5,
            splitLine: {
              show: false
            }
          },
          {
            type: 'value',
            name: '金额',
            scale: true,
            min: function(value) {
              // 设置Y轴起始点为数据最小值的0.9倍，增强变化可见性
              return Math.floor(value.min * 0.9);
            },
            axisLabel: {
              formatter: (value) => {
                return (value / 10000).toFixed(0) + '万'
              }
            },
            // 金额轴不显示网格线
            splitNumber: 5,
            splitLine: {
              show: false
            }
          }
        ],
        series: [
          {
            name: '余额',
            type: 'line',
            smooth: true,
            data: balances,
            yAxisIndex: 0,
            lineStyle: {
              color: '#5470c6',
              width: 1.0
            },
            symbol: 'circle',
            symbolSize: 4,
            emphasis: {
              symbolSize: 6
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(84, 112, 198, 0.5)' },
                { offset: 1, color: 'rgba(84, 112, 198, 0.1)' }
              ])
            }
          },
          {
            name: '金额',
            type: 'bar',
            data: amounts,
            yAxisIndex: 1,
            barWidth: '10%',
            itemStyle: {
              color: (params) => {
                // 根据金额正负设置不同颜色
                const value = params.value
                return value >= 0 ? '#91cc75' : '#fac858'
              }
            }
          }
        ]
      }
      
      chart.setOption(option)
    }

    //call loadTenants if isSuperTenant
    const loadTenants = async () => {
      console.error('当前租户ID:', currentTenantId.value)
      console.error('是否超租户:', isSuperTenant)
      if (!isSuperTenant) return
      try {
        console.log('开始加载租户列表')
        const res = await tenantService.getTenantList({ pageNum: 1, pageSize: 1000, keyword: '' })
        console.log('租户列表API响应:', res)
        // 检查响应是否是成功的结构
        if (res && res.code === 200) {
          tenants.value = res.data || []
          console.log('租户列表数据:', tenants.value)
        } else {
          console.error('获取租户列表失败:', res.msg || '未知错误')
          tenants.value = []
        }
      } catch (e) {
        console.error('加载租户列表失败:', e)
        ElMessage.error(e.message || '加载租户列表失败')
      }
    }

    // 加载账户信息、交易记录、卡片、协议列表、租户列表
    const load = async () => {
      try {
        console.log('开始加载账户信息...')
        const res = await accountService.get(false)
        console.log('账户信息API响应:', res)
        const a = res.data || {}
        console.log('账户数据:', a)
        form.accountName = a.accountName || ''
        form.accountNo = a.accountNo || ''
        form.cumInvestAmount = a.cumInvestAmount || 0
        form.cumRecoveredAmount = a.cumRecoveredAmount || 0
        form.cumWithdrawAmount = a.cumWithdrawAmount || 0
        form.availableBalance = a.availableBalance || 0
        const c = await accountService.listCards()
        cards.value = c.data || []
        await loadTx()
        await loadAgreements()
        await loadTenants()
        await loadInvestedProjects()
      } catch (e) {
        ElMessage.error(e.message || '加载失败')
      }
    }

    // 加载账户可用协议列表
    const loadAgreements = async () => {
      try {
        const res = await accountService.getAvailableAgreements()
        agreements.value = res.data || []
      } catch (e) {
        ElMessage.error(e.message || '加载协议列表失败')
      }
    }

    // 加载已投项目列表
    const loadInvestedProjects = async () => {
      try {
        const res = await accountService.listInvestedProjects()
        investedProjects.value = res.data || []
      } catch (e) {
        ElMessage.error(e.message || '加载已投项目列表失败')
      }
    }

    // 重新计算账户余额
    const recalc = async () => {
      try {
        const res = await accountService.get(true)
        const a = res.data || {}
        form.cumInvestAmount = a.cumInvestAmount || 0
        form.cumRecoveredAmount = a.cumRecoveredAmount || 0
        form.cumWithdrawAmount = a.cumWithdrawAmount || 0
        form.availableBalance = a.availableBalance || 0
        ElMessage.success('已重新计算')
      } catch (e) {
        ElMessage.error(e.message || '计算失败')
      }
    }

    // 保存账户信息
    const save = async () => {
      try {
        await accountService.createOrUpdate({ accountName: form.accountName, accountNo: form.accountNo })
        ElMessage.success('保存成功')
      } catch (e) {
        ElMessage.error(e.message || '保存失败')
      }
    }

    // 新增账户卡片
    const addCard = async () => {
      try {
        await accountService.addCard(cardForm)
        ElMessage.success('新增成功')
        showCardDialog.value = false
        cardForm.bankName = ''; cardForm.cardNo=''; cardForm.holderName=''
        const c = await accountService.listCards()
        cards.value = c.data || []
      } catch (e) {
        ElMessage.error(e.message || '新增失败')
      }
    }

    // 删除账户卡片
    const removeCard = async (id) => {
      try {
        await accountService.removeCard(id)
        ElMessage.success('删除成功')
        const c = await accountService.listCards()
        cards.value = c.data || []
      } catch (e) {
        ElMessage.error(e.message || '删除失败')
      }
    }

    // 判断字段类型
    const getFieldType = (field, sampleValue) => {
      if (field.includes('Date') || field.includes('At')) {
        return 'date'
      } else if (field.includes('Amount') || field.includes('amount') || field.includes('Balance') || field.includes('balance')) {
        return 'amount'
      } else if (typeof sampleValue === 'number') {
        return 'number'
      } else if (typeof sampleValue === 'boolean') {
        return 'boolean'
      } else {
        return 'string'
      }
    }
    
    // 提取动态列
    const extractDynamicColumns = (data) => {
      if (!data || data.length === 0) return []
      
      // 严格按照数据库表结构中的字段顺序
      const tableFieldOrder = [
        'id', 'tenantId', 'accountNo', 'txType', 'direction', 
        'amount', 'balanceAfter', 'occurredAt', 'refAgreementNo', 
        'storeIncomeDate', 'devideRate', 'tenantShareRatio', 
        'refStoreEntCode', 'refSerialId', 'memo', 'createdAt'
      ]
      
      // 字段中文标签映射
      const fieldLabelMap = {
        'id': '主键ID',
        'tenantId': '租户ID',
        'accountNo': '账户号',
        'txType': '交易类型',
        'direction': '资金方向',
        'amount': '交易金额',
        'balanceAfter': '交易后余额',
        'occurredAt': '交易发生时间',
        'refAgreementNo': '关联协议号',
        'storeIncomeDate': '门店经营日期',
        'devideRate': '协议分账比例',
        'tenantShareRatio': '租户占比',
        'refStoreEntCode': '关联门店企业编码',
        'refSerialId': '外部流水号',
        'memo': '备注说明',
        'createdAt': '记录创建时间'
      }
      
      // 获取所有唯一字段
      const allFields = new Set()
      data.forEach(item => {
        Object.keys(item).forEach(key => allFields.add(key))
      })
      
      // 按照表结构顺序筛选出实际存在的字段
      const orderedFields = []
      const remainingFields = new Set(allFields)
      
      // 先添加表结构中定义的字段（如果存在）
      tableFieldOrder.forEach(field => {
        if (remainingFields.has(field)) {
          orderedFields.push(field)
          remainingFields.delete(field)
        }
      })
      
      // 如果还有未在表结构中定义的字段，按字母顺序添加到末尾
      if (remainingFields.size > 0) {
        orderedFields.push(...[...remainingFields].sort())
      }
      
      // 确保所有记录都包含相同的字段集合
      data.forEach(item => {
        orderedFields.forEach(field => {
          if (item[field] === undefined) {
            item[field] = null
          }
        })
      })
      
      // 生成动态列定义
      return orderedFields.map(field => {
        // 获取字段类型
        const sampleValue = data.find(item => item[field] != null)?.[field]
        const fieldType = getFieldType(field, sampleValue)
        
        return {
          prop: field,
          label: fieldLabelMap[field] || field.replace(/([A-Z])/g, ' $1').trim().replace(/^\w/, c => c.toUpperCase()),
          type: fieldType,
          align: fieldType === 'amount' || fieldType === 'number' ? 'right' : 'center'
        }
      })
    }
    
    // 加载账户交易记录
    const loadTx = async () => {
      try {
        const params = {
          startDate: dateRange.value?.[0],
          endDate: dateRange.value?.[1],
          types: types.value,
          pageNum: pageNum.value,
          pageSize: pageSize.value
        }
        const res = await accountService.listTx(params)
        total.value = res.data?.total || 0
        txList.value = res.data?.list || []
        
        // 调试：打印后端返回的实际字段结构
        console.log('后端返回的交易数据:', txList.value)
        if (txList.value.length > 0) {
          console.log('第一条交易记录的所有字段:', Object.keys(txList.value[0]))
        }
        
        // 提取动态列
        dynamicColumns.value = extractDynamicColumns(txList.value)
        console.log('生成的动态列:', dynamicColumns.value)
        
        // 加载图表数据
        await loadChartData()
        
        // 渲染图表
        setTimeout(() => renderChart(), 0)
      } catch (e) {
        ElMessage.error(e.message || '查询失败')
      }
    }

    // 分页查询交易记录
    const onPageChange = async (p) => {
      pageNum.value = p
      await loadTx()
    }

    // 导出交易记录为CSV
    const exportCsv = async () => {
      try {
        const params = {
          startDate: dateRange.value?.[0],
          endDate: dateRange.value?.[1],
          types: types.value
        }
        const res = await accountService.exportTx(params)

        const header = ['流水发生时间','交易类型','交易金额','账户余额','门店编码','协议号','门店经营日期','协议分账比例']
        const rows = (res.data || []).map(t => [
          formatDate(t.occurredAt),
          txTypeMap[t.txType] || t.txType,
          t.amount,
          t.balanceAfter,
          t.refStoreEntCode,
          t.refAgreementNo,
          formatDate(t.storeIncomeDate),
          t.devideRate
        ])
        const content = [header, ...rows].map(r => r.join(',')).join('\n')
        const blob = new Blob(['\ufeff' + content], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'account-transactions.csv'
        a.click()
        URL.revokeObjectURL(url)
      } catch (e) {
        ElMessage.error(e.message || '导出失败')
      }
    }

    // 提现操作
    const doWithdraw = async () => {
      try {
        const amt = Number(withdrawForm.amount)
        if (!amt || amt <= 0) { ElMessage.error('请输入有效金额'); return }
        await accountService.withdraw({ amount: amt, memo: withdrawForm.memo })
        ElMessage.success('提现已入账')
        showWithdraw.value = false
        withdrawForm.amount=''; withdrawForm.memo=''
        await recalc()
        await loadTx()
      } catch (e) {
        ElMessage.error(e.message || '提现失败')
      }
    }

    // 出资操作
    const doInvest = async () => {
      try {
        if (!investForm.agreementNo) { ElMessage.error('请选择协议'); return }
        const amt = Number(investForm.amount)
        if (!amt || amt <= 0) { ElMessage.error('请输入有效金额'); return }
        
        const investParams = {
          amount: amt,
          agreementNo: investForm.agreementNo,
          memo: investForm.memo
        }
        
        // 如果是super tenant且选择了目标租户，则添加targetTenantId参数
        if (isSuperTenant && investForm.targetTenantId) {
          investParams.targetTenantId = investForm.targetTenantId
        }
        
        await accountService.invest(investParams)
        ElMessage.success('出资成功')
        showInvest.value = false
        investForm.agreementNo=''; investForm.amount=''; investForm.memo=''; investForm.targetTenantId=''
        await recalc()
        await loadTx()
      } catch (e) {
        ElMessage.error(e.message || '出资失败')
      }
    }
    // 充值操作
    const doRecharge = async () => {
      try {
        const amt = Number(rechargeForm.amount)
        if (!amt || amt <= 0) { ElMessage.error('请输入有效金额'); return }
        
        const rechargeParams = {
          amount: amt,
          memo: rechargeForm.memo
        }
        
        await accountService.recharge(rechargeParams)
        ElMessage.success('充值成功')
        showRecharge.value = false
        rechargeForm.amount=''; rechargeForm.memo=''
        await recalc()
        await loadTx()
      } catch (e) {
        ElMessage.error(e.message || '充值失败')
      }
    }

    // 组件挂载时初始化查询
    onMounted(async () => {
      const today = new Date()
      const start = new Date(today.getTime() - 30 * 24 * 3600 * 1000)
      const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
      dateRange.value = [fmt(start), fmt(today)]
      await load()
    })

     // 权限处理
    const permissionCodes = computed(() => {
      const permissions = store.state.permissions
      return permissions ? Object.values(permissions).map(perm => perm.permissionCode) : []
    })

    const hasPermission = (code) => {
      return permissionCodes.value.includes(code)
    }
    
    // 组件卸载时清理图表
    onUnmounted(() => {
      if (chart) {
        chart.dispose()
        chart = null
      }
    })
    return { form, cards, showCardDialog, cardForm, dateRange, types, txList, pageNum, pageSize, total, showWithdraw, withdrawForm, showInvest, investForm, showRecharge, rechargeForm, agreements, tenants, isSuperTenant, chartRef, txTypeMap,
      dynamicColumns, currency, formatCellValue, save, recalc, addCard, removeCard, loadTx, onPageChange, exportCsv, doWithdraw, doInvest, doRecharge, loadAgreements, loadTenants, loadInvestedProjects, investedProjects, renderChart, hasPermission, formatDate}
  }
}
</script>

<style scoped>
.account-management { padding: 16px; }
.stats { display:grid; grid-template-columns: repeat(2,1fr); gap:16px; margin-top:16px; }
.stat-card {
  background: #fafafa;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}
.stat-label {
  font-size: 12px;
  color: #666;
  margin-bottom: 8px;
}
.stat-value {
  font-size: 24px;
  color: #1d1d1f;
  font-weight: 600;
}
.account-title { font-size: 13px; color: #666; }
</style>
